import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ReferenceStock } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import { RootState } from 'app/AppStore';
import { getPricesMap, getStocksMapV2, IAMReferenceLocal, ReferencePriceType } from 'domains/references';
import {
  sortStocksBySearchStatus,
  sortStocksByWarehouse,
  stocksMapToArray,
} from 'domains/references/References.mapper';
import { isLoading, NO_DATA, NOT_FOUND, SearchData } from 'utils';

export function sortIAMReferencesByReferenceNumbers(
  references: IAMReferenceLocal[],
  referenceNumbers: string[],
): IAMReferenceLocal[] {
  return references.sort(
    (a, b) => referenceNumbers.indexOf(a.referenceNumber) - referenceNumbers.indexOf(b.referenceNumber),
  );
}

export function sortIAMReferences(
  references: IAMReferenceLocal[],
  stockData: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>,
): IAMReferenceLocal[] {
  const referenceNumbers = new Set(references.map((reference) => reference.referenceNumber));
  const filteredStockData: Map<string, SearchData<Map<string, ReferenceStock>> | undefined> = new Map(
    [...stockData].filter(([referenceNumber]) => referenceNumbers.has(referenceNumber)),
  );
  const stocksArray = stocksMapToArray(filteredStockData);
  const sortedStocks = [...stocksArray].sort(sortStocksBySearchStatus).sort(sortStocksByWarehouse);

  return sortIAMReferencesByReferenceNumbers(
    references,
    sortedStocks.map((stock) => stock.data.reference),
  );
}

export const getFilteredMotrioReferences = (
  refs: IAMReferenceLocal[],
  prices: Map<string, NO_DATA | ReferencePriceType>,
  stocks: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>,
  includePriceless = false,
): IAMReferenceLocal[] => {
  const referenceNumbers = new Set(refs.map((reference) => reference.referenceNumber));
  const filteredPrices: Map<string, NO_DATA | ReferencePriceType> = new Map(
    [...prices].filter(([referenceNumber]) => referenceNumbers.has(referenceNumber)),
  );
  const filteredStocks: Map<string, SearchData<Map<string, ReferenceStock>> | undefined> = new Map(
    [...stocks].filter(([referenceNumber]) => referenceNumbers.has(referenceNumber)),
  );
  const motrioReferences = refs.filter((r) => r.supplier === 'MOTRIO');
  return motrioReferences.filter((ref) => {
    return shouldDisplayIAMReference(
      ref,
      filteredPrices.get(ref.referenceNumber),
      filteredStocks.get(ref.referenceNumber),
      includePriceless,
    );
  });
};

export const getFilteredOtherBrandsReferences = (refs: IAMReferenceLocal[]): IAMReferenceLocal[] => {
  return refs.filter((r) => r.supplier !== 'MOTRIO');
};

export function shouldDisplayIAMReference(
  reference: IAMReferenceLocal,
  price: NO_DATA | ReferencePriceType,
  stock: SearchData<Map<string, ReferenceStock>> | undefined,
  includePriceless = false,
): boolean {
  if (reference.supplier === 'MOTRIO') {
    return stock?.searchStatus !== NOT_FOUND && (includePriceless || price !== NOT_FOUND);
  }
  return true;
}

export const useMotrioStockPricesLoading = (referenceNumbers: string[]): boolean => {
  const motrioPrices = useSelector((state: RootState) => getPricesMap(state, referenceNumbers ?? []));
  const motrioStocks = useSelector((state: RootState) => getStocksMapV2(state, referenceNumbers ?? []));

  const motrioStocksPrices = useMemo(() => {
    return [...motrioPrices.values(), ...[...motrioStocks.values()].map((s) => s?.searchStatus)];
  }, [motrioPrices, motrioStocks]);

  return motrioStocksPrices.some((sp) => isLoading(sp));
};
